import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { createDialog } from '@patsnap/tahiti-core';
import type { IpProfileExternalService } from '../ip-profile-external-service';
import { HomeIpApi } from '@/domains/home/apis/home-ip-api';
import { prepareTokenDeferred } from '@telecom/shared/startup/defer';

/**
 * 创建 IP Profile External 确认对话框
 */
const createIpProfileExternalDialog = createDialog(
    () => import('@/presentation/pages/home/components/IpProfileExternalDialog.vue'),
);

/**
 * IP Profile External 服务实现
 */
@injectable()
export class IpProfileExternalServiceImpl implements IpProfileExternalService {
    private _dialog = createIpProfileExternalDialog();

    constructor(
        @inject(HomeIpApi)
        private homeIpApi: HomeIpApi,
    ) {}

    /**
     * 处理 IP Profile External 点击事件
     */
    async handleClick(): Promise<void> {
        return new Promise((resolve, reject) => {
            // 注册提交事件处理
            this._dialog.onSubmit(async (data, next) => {
                if (!data.confirmed) {
                    next();
                    reject(new Error('用户取消操作'));
                    return;
                }

                try {
                    // 获取用户 ID
                    const userId = await this.getCurrentUserId();
                    if (!userId) {
                        throw new Error('无法获取当前用户ID');
                    }

                    // 获取认证 token
                    const authToken = await this.homeIpApi.getIpProfileToken();
                    if (!authToken) {
                        throw new Error('无法获取认证token');
                    }

                    // 构建外部 URL 并打开
                    const externalUrl = `https://ip-profile.com/Agent?user_id=${userId}&auth_token=${authToken}`;
                    window.open(externalUrl, '_blank');

                    next(); // 关闭对话框
                    resolve();
                } catch (error) {
                    console.error('跳转到 IP Profile External 失败:', error);
                    next(false); // 保持对话框打开
                    reject(error);
                }
            });

            // 注册取消事件处理
            this._dialog.onCancel(() => {
                reject(new Error('用户取消操作'));
            });

            // 打开对话框
            this._dialog.open({});
        });
    }

    /**
     * 从 JWT token 中获取当前用户 ID
     */
    private async getCurrentUserId(): Promise<string> {
        try {
            const token = await this.getToken();
            if (!token) {
                throw new Error('Token is empty');
            }

            // 解析 JWT token
            const parts = token.split('.');
            if (parts.length !== 3) {
                throw new Error('Invalid JWT token format');
            }

            // Base64 解码
            const payload = JSON.parse(
                decodeURIComponent(
                    escape(
                        window.atob(
                            parts[1]
                                .replace(/-/g, '+')
                                .replace(/_/g, '/'),
                        ),
                    ),
                ),
            );

            return payload.sub || '';
        } catch (error) {
            console.error('获取用户ID失败:', error);
            return '';
        }
    }

    /**
     * 获取 JWT token
     * 从 passport 实例获取 access token
     */
    private async getToken(): Promise<string> {
        const passport = await prepareTokenDeferred.promise;
        return passport?.getAccessToken().then(
            (token) => {
                if (!token) {
                    throw new Error('Token is empty');
                }
                return token;
            },
            () => {
                throw new Error('Failed to get access token');
            },
        );
    }
}
